import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, debounce, IconButton, Stack, Tooltip } from '@mui/material'

import TextField from 'components/global/CUK/TextField'

import useFilters, { useResetFilters } from '../store'
import { VISIBILITY_VALUES } from '../constants'
import { SitesUnscopeFilters, VisibilityInCenter } from '../types'
import AccountSelect from './AccountSelect'
import SingleFilterSelect, { SingleFilterOption } from './SingleFilterSelect'
import StateSelect from './StateSelect'

const VERBATIM_DEBOUNCE_DELAY = 500

const FILTERS_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
  columnGap: 2,
  rowGap: 1,
  flex: 1,
  minWidth: 0,
  maxWidth: 960
}

const Filters = () => {
  const { t } = useTranslation()
  const [filters, setFilters] = useFilters()
  const resetFilters = useResetFilters()
  const [verbatim, setVerbatim] = useState(filters.verbatim)

  const handleFilterChange = <K extends keyof SitesUnscopeFilters>(key: K) =>
    (value: SitesUnscopeFilters[K]) => {
      setFilters(previousFilters => ({ ...previousFilters, [key]: value }))
    }

  const debouncedVerbatimChange = useMemo(
    () => debounce((value: string) => {
      setFilters(previousFilters => ({ ...previousFilters, verbatim: value }))
    }, VERBATIM_DEBOUNCE_DELAY),
    [setFilters]
  )

  useEffect(() => () => debouncedVerbatimChange.clear(), [debouncedVerbatimChange])

  const handleVerbatimChange = (value: string | number) => {
    const nextVerbatim = value.toString()
    setVerbatim(nextVerbatim)
    debouncedVerbatimChange(nextVerbatim)
  }

  const handleResetFilters = () => {
    debouncedVerbatimChange.clear()
    setVerbatim('')
    resetFilters()
  }

  const booleanOptions = useMemo<SingleFilterOption<boolean>[]>(
    () => [
      { id: 'yes', label: t('admin_sites.filters.yes'), value: true },
      { id: 'no', label: t('admin_sites.filters.no'), value: false }
    ],
    [t]
  )

  const visibilityOptions = useMemo<SingleFilterOption<VisibilityInCenter>[]>(
    () => VISIBILITY_VALUES.map(visibility => ({
      id: visibility,
      label: t(`admin_sites.visibility.${visibility}`),
      value: visibility
    })),
    [t]
  )

  return (
    <Stack direction="row" gap={2} alignItems="flex-start" sx={{ mb: 2 }}>
      <Box sx={FILTERS_GRID_SX}>
        <Box minWidth={0}>
          <TextField
            value={verbatim}
            setvalue={handleVerbatimChange}
            width="100%"
            type="text"
            input={{
              placeholder: t('admin_sites.filters.search_placeholder'),
              starticon: <i className={'fa-light fa-search'} aria-hidden="true" />
            }}
          />
        </Box>
        <Box minWidth={0}>
          <AccountSelect value={filters.account} onChange={handleFilterChange('account')} />
        </Box>
        <Box minWidth={0}>
          <StateSelect filters={filters} onChange={handleFilterChange('state')} />
        </Box>
        <Box minWidth={0}>
          <SingleFilterSelect
            placeholder={t('admin_sites.filters.has_active_project')}
            options={booleanOptions}
            value={filters.has_active_project}
            onChange={handleFilterChange('has_active_project')}
          />
        </Box>
        <Box minWidth={0}>
          <SingleFilterSelect
            placeholder={t('admin_sites.filters.has_process')}
            options={booleanOptions}
            value={filters.has_process}
            onChange={handleFilterChange('has_process')}
          />
        </Box>
        <Box minWidth={0}>
          <SingleFilterSelect
            placeholder={t('admin_sites.filters.visibility_in_center')}
            options={visibilityOptions}
            value={filters.visibility_in_center}
            onChange={handleFilterChange('visibility_in_center')}
          />
        </Box>
      </Box>
      <Tooltip title={t('ui_elements.reset')}>
        <Box component="span">
          <IconButton
            aria-label={t('ui_elements.reset')}
            onClick={handleResetFilters}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              border: '1px solid #EFEFEF',
              color: 'content.secondary',
              fontSize: 14,
              '&:hover': { bgcolor: 'rgba(37, 112, 235, 0.06)' }
            }}
          >
            <i className="fa-regular fa-arrows-rotate" aria-hidden="true" />
          </IconButton>
        </Box>
      </Tooltip>
    </Stack>
  )
}

export default Filters
